import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";

import { useAuth } from "../../contexts/AuthContext";
import { useTheme } from "../../contexts/ThemeContext";
import { useIsMobile } from "../../hooks/useIsMobile";
import LayoutDesktop from "../../layouts/LayoutDesktop";
import LayoutMobile from "../../layouts/LayoutMobile";

import { db } from "../../config/firebase";
import { STATUS_ATENDIMENTO } from "../../config/status";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { uploadToStorage } from "../../utils/storageUpload";

function normalizarTipoResposta(tipo) {
  const t = String(tipo || "").toUpperCase();
  if (t === "SIM/NAO" || t === "SIM_NAO") return "SIM_NAO";
  if (t === "OK/NOK" || t === "OK_NOK") return "OK_NOK";
  if (t === "NUM" || t === "NUMERO") return "NUMERO";
  if (t === "TEXT" || t === "TEXTO") return "TEXTO";
  if (t === "BAR") return "BAR";
  return t || "SIM_NAO";
}

function tituloTipoResposta(tipo) {
  const t = normalizarTipoResposta(tipo);
  if (t === "SIM_NAO") return "Sim / Não";
  if (t === "OK_NOK") return "OK / NOK";
  if (t === "NUMERO") return "Número";
  if (t === "TEXTO") return "Texto";
  if (t === "BAR") return "Bar";
  return t;
}

function criarRespostaBase(item) {
  return {
    pergunta: item?.pergunta || "",
    tipoResposta: normalizarTipoResposta(item?.tipoResposta),
    valor: "",
    obs: "",
    fotoUrl: item?.fotoUrl || null,
  };
}

export default function ChecklistExecutarOsPage() {
  const { osId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const { usuario: usuarioLogado } = useAuth();
  const { theme } = useTheme();

  // Determinar para onde voltar
  const voltarPara = location?.state?.voltarPara || null;
  const navegarVoltar = () => navigate(voltarPara ? voltarPara : -1);

  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");
  const [os, setOs] = useState(null);
  const [atendimentoId, setAtendimentoId] = useState(null);
  const [observacoes, setObservacoes] = useState("");
  const [respostas, setRespostas] = useState([]);
  const [salvando, setSalvando] = useState(false);
  const [fotos, setFotos] = useState([]);
  const [uploadandoFotos, setUploadandoFotos] = useState(false);
  const [msgSucesso, setMsgSucesso] = useState("");

  const itensChecklist = useMemo(() => {
    const itens = os?.checklistItens || [];
    return Array.isArray(itens) ? itens : [];
  }, [os]);

  useEffect(() => {
    let cancelado = false;

    async function carregar() {
      try {
        setLoading(true);
        setErro("");

        if (!usuarioLogado?.uid) {
          setErro("Você precisa estar logado.");
          return;
        }
        if (!osId) {
          setErro("OS inválida.");
          return;
        }

        const osRef = doc(db, "ordensServico", osId);
        const osSnap = await getDoc(osRef);
        if (!osSnap.exists()) {
          setErro("OS não encontrada.");
          return;
        }

        let osData = { id: osSnap.id, ...osSnap.data() };
        
        console.log("OS carregada:", osData);
        console.log("checklistItens na OS:", osData.checklistItens);
        console.log("checklistModeloId:", osData.checklistModeloId);
        
        // Se a OS não tem checklistItens mas tem checklistModeloId, carregar do modelo
        if ((!osData.checklistItens || osData.checklistItens.length === 0) && osData.checklistModeloId) {
          console.log("Carregando modelo de checklist:", osData.checklistModeloId);
          try {
            const modeloRef = doc(db, "checklistsModelos", osData.checklistModeloId);
            const modeloSnap = await getDoc(modeloRef);
            if (modeloSnap.exists()) {
              const modeloData = modeloSnap.data();
              console.log("Modelo carregado:", modeloData);
              osData = {
                ...osData,
                checklistItens: modeloData.itens || [],
                checklistModeloNome: modeloData.nome || osData.checklistModeloNome,
              };
              console.log("OS atualizada com itens do modelo:", osData.checklistItens);
            } else {
              console.warn("Modelo de checklist não encontrado");
            }
          } catch (e) {
            console.warn("Erro ao carregar modelo de checklist:", e);
          }
        }
        
        if (cancelado) return;
        setOs(osData);

        // Busca atendimento em andamento (se existir) para salvar o checklist junto do relatório.
        const atendQ = query(
          collection(db, "atendimentos"),
          where("osId", "==", osId),
          where("statusAtendimento", "==", STATUS_ATENDIMENTO.EM_ANDAMENTO),
          orderBy("dataInicio", "desc"),
          limit(1)
        );
        const atendSnap = await getDocs(atendQ);
        const atendDoc = atendSnap.docs?.[0] || null;
        const atendData = atendDoc ? { id: atendDoc.id, ...atendDoc.data() } : null;
        if (cancelado) return;
        setAtendimentoId(atendData?.id || null);

        const respostasExistentes =
          atendData?.checklistRespostas?.respostas ||
          osData?.checklistUltimaExecucao?.respostas ||
          [];
        const obsExistente =
          atendData?.checklistObservacoes ||
          osData?.checklistUltimaExecucao?.observacoes ||
          "";

        // Use itens do arquivo osData (que pode ter sido carregado do modelo)
        const itensParaUsarBase = osData.checklistItens || [];
        const base = itensParaUsarBase.map((item) => {
          const pergunta = (item?.pergunta || "").trim();
          const encontrada = Array.isArray(respostasExistentes)
            ? respostasExistentes.find((r) => (r?.pergunta || "").trim() === pergunta)
            : null;

          if (!encontrada) return criarRespostaBase(item);
          return {
            pergunta,
            tipoResposta: normalizarTipoResposta(encontrada?.tipoResposta || item?.tipoResposta),
            valor: encontrada?.valor ?? "",
            obs: encontrada?.obs ?? "",
            fotoUrl: item?.fotoUrl || encontrada?.fotoUrl || null,
          };
        });

        setObservacoes(obsExistente || "");
        setRespostas(base);
      } catch (e) {
        console.error(e);
        setErro("Falha ao carregar checklist.");
      } finally {
        if (!cancelado) setLoading(false);
      }
    }

    carregar();
    return () => {
      cancelado = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [osId, usuarioLogado?.uid]);

  function setResposta(index, patch) {
    setRespostas((prev) => {
      const novo = [...prev];
      novo[index] = { ...(novo[index] || {}), ...patch };
      return novo;
    });
  }

  function validar() {
    for (let i = 0; i < respostas.length; i++) {
      const r = respostas[i];
      const t = normalizarTipoResposta(r?.tipoResposta);
      const v = String(r?.valor ?? "").trim();
      const pergunta = (r?.pergunta || "(sem pergunta)").trim();

      if (t === "SIM_NAO" && !["SIM", "NAO"].includes(v.toUpperCase())) {
        return `Responda: "${pergunta}" (Sim/Não).`;
      }
      if (t === "OK_NOK" && !["OK", "NOK"].includes(v.toUpperCase())) {
        return `Responda: "${pergunta}" (OK/NOK).`;
      }
      if ((t === "TEXTO" || t === "NUMERO" || t === "BAR") && !v) {
        return `Responda: "${pergunta}".`;
      }
    }
    return "";
  }

  async function handleAdicionarFotos(event) {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const tiposFotoPermitidos = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    setUploadandoFotos(true);
    setErro("");
    setMsgSucesso("");

    const novasFotos = [];

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (!file.type.startsWith("image/")) continue;

        if (!tiposFotoPermitidos.includes(String(file.type || "").toLowerCase())) {
          setErro(`Formato não suportado para ${file.name}. Use JPG, PNG ou WEBP.`);
          continue;
        }

        const maxSize = 10 * 1024 * 1024;
        if (file.size > maxSize) {
          setErro(`Arquivo ${file.name} é muito grande (máximo 10MB).`);
          continue;
        }

        const url = await uploadToStorage({
          grupoId: usuarioLogado?.grupoId,
          empresaId: os?.empresaId || null,
          pasta: "checklists_fotos",
          file,
          filenamePrefix: `checklist_os_${osId}_${Date.now()}`,
        });

        novasFotos.push({
          url,
          nome: file.name,
          uploadEm: new Date().toISOString(),
        });
      }

      if (novasFotos.length > 0) {
        setFotos((prev) => [...prev, ...novasFotos]);
        setMsgSucesso(`${novasFotos.length} foto(s) adicionada(s) com sucesso!`);
      }
    } catch (e) {
      console.error("Erro ao adicionar fotos do checklist da OS:", e);
      setErro(`Erro ao fazer upload das fotos: ${e?.message || e}`);
    } finally {
      setUploadandoFotos(false);
      event.target.value = "";
    }
  }

  function removerFoto(idx) {
    setFotos((prev) => prev.filter((_, i) => i !== idx));
  }

  async function salvar() {
    try {
      setErro("");
      const msg = validar();
      if (msg) {
        setErro(msg);
        return;
      }

      setSalvando(true);

      const payload = {
        tipoChecklist: os?.checklistTipo || "INSTALACAO",
        checklistNome: os?.checklistNome || os?.checklistModeloNome || "Checklist",
        checklistModeloId: os?.checklistModeloId || null,
        checklistModeloNome: os?.checklistModeloNome || null,
        respostas: respostas.map((r) => ({
          pergunta: (r?.pergunta || "").trim(),
          tipoResposta: normalizarTipoResposta(r?.tipoResposta),
          valor: r?.valor ?? "",
          obs: r?.obs ?? "",
          fotoUrl: r?.fotoUrl || null,
        })),
        observacoes: observacoes || "",
        fotos: fotos.map((f) => ({ url: f.url, nome: f.nome, uploadEm: f.uploadEm })) || [],
      };

      const osRef = doc(db, "ordensServico", osId);
      const osUpdate = {
        checklistUltimaExecucao: {
          ...payload,
          atendimentoId: atendimentoId || null,
          executadoEm: serverTimestamp(),
        },
        checklistConcluido: true,
        checklistConcluidoEm: serverTimestamp(),
        checklistConcluidoAtendimentoId: atendimentoId || null,
        atualizadoEm: serverTimestamp(),
      };

      if (atendimentoId) {
        const atendRef = doc(db, "atendimentos", atendimentoId);
        await updateDoc(atendRef, {
          checklistRespostas: payload,
          checklistObservacoes: observacoes || "",
          checklistExecutadoEm: serverTimestamp(),
          atualizadoEm: serverTimestamp(),
        });
      }

      await updateDoc(osRef, osUpdate);
      setMsgSucesso("Checklist salvo com sucesso!");
      navegarVoltar();
    } catch (e) {
      console.error(e);
      setErro("Falha ao salvar checklist.");
    } finally {
      setSalvando(false);
    }
  }

  const header = (
    <header style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
      <div>
        <h3 style={{ margin: 0 }}>Checklist</h3>
        <p style={{ marginTop: 6, opacity: 0.85 }}>
          OS: {os?.codigo || os?.id}
          {os?.academiaNome ? ` · ${os.academiaNome}` : ""}
        </p>
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button type="button" className="pc-btn-secundario" onClick={navegarVoltar}>
          Voltar
        </button>
        <button type="button" className="pc-btn-primario" onClick={salvar} disabled={salvando || loading}>
          {salvando ? "Salvando..." : "Salvar"}
        </button>
      </div>
    </header>
  );

  const body = (
    <section className="pc-card" style={{ maxWidth: 980, margin: "0 auto" }}>
      <div>
        {header}
      </div>

      {msgSucesso && (
        <div
          style={{
            marginTop: 12,
            padding: 10,
            borderRadius: 10,
            background: "#14532d",
            color: "#bbf7d0",
            fontSize: 14,
          }}
        >
          {msgSucesso}
        </div>
      )}

      {erro && (
        <div
          style={{
            marginTop: 12,
            padding: 10,
            borderRadius: 10,
            background: "#7f1d1d",
            color: "#fecaca",
            fontSize: 14,
          }}
        >
          {erro}
        </div>
      )}

      <div style={{ marginTop: 14 }}>
        {loading ? (
          <p style={{ padding: 10 }}>Carregando...</p>
        ) : !os ? (
          <p style={{ padding: 10 }}>OS não encontrada.</p>
        ) : itensChecklist.length === 0 ? (
          <p style={{ padding: 10 }}>Essa OS não possui checklist.</p>
        ) : (
          <div style={{ display: "grid", gap: 12 }}>

          {itensChecklist.map((item, idx) => {
            const r = respostas[idx] || criarRespostaBase(item);
            const tipo = normalizarTipoResposta(r.tipoResposta || item.tipoResposta);
            const valor = String(r.valor ?? "");
            const pergunta = String(item?.pergunta || "").trim() || `Item ${idx + 1}`;

            return (
              <div
                key={`${idx}_${item?.pergunta || ""}`}
                style={{
                  padding: 12,
                  borderRadius: 12,
                  border: "1px solid rgba(148,163,184,0.35)",
                  background: "rgba(255,255,255,0.04)",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                  <strong>{idx + 1}. {pergunta}</strong>
                </div>

                {tipo === "SIM_NAO" && (
                  <div style={{ marginTop: 10 }}>
                    <select
                      className="pc-input"
                      style={{ width: "100%" }}
                      value={valor}
                      onChange={(e) => setResposta(idx, { valor: e.target.value })}
                    >
                      <option value="">Selecione...</option>
                      <option value="SIM">Sim</option>
                      <option value="NAO">Não</option>
                    </select>
                  </div>
                )}

                {tipo === "OK_NOK" && (
                  <div style={{ marginTop: 10 }}>
                    <select
                      className="pc-input"
                      style={{ width: "100%" }}
                      value={valor}
                      onChange={(e) => setResposta(idx, { valor: e.target.value })}
                    >
                      <option value="">Selecione...</option>
                      <option value="OK">OK</option>
                      <option value="NOK">NOK</option>
                    </select>
                  </div>
                )}

                {(tipo === "NUMERO" || tipo === "BAR") && (
                  <div style={{ marginTop: 10 }}>
                    <input
                      className="pc-input"
                      style={{ width: "100%" }}
                      type="number"
                      value={valor}
                      onChange={(e) => setResposta(idx, { valor: e.target.value })}
                      placeholder="Digite o valor"
                    />
                  </div>
                )}

                {tipo === "TEXTO" && (
                  <div style={{ marginTop: 10 }}>
                    <textarea
                      className="pc-input"
                      style={{ width: "100%" }}
                      rows={3}
                      value={valor}
                      onChange={(e) => setResposta(idx, { valor: e.target.value })}
                      placeholder="Digite..."
                    />
                  </div>
                )}

                {item?.fotoUrl && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 6 }}>Foto de referência</div>
                    <img
                      src={item.fotoUrl}
                      alt="foto"
                      style={{ maxWidth: "100%", borderRadius: 8 }}
                    />
                  </div>
                )}
              </div>
            );
          })}
          </div>
        )}

        <div style={{ marginTop: 16 }}>
          <label style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>
            Observações (opcional)
          </label>
          <textarea
            rows={3}
            className="pc-input"
            value={observacoes}
            onChange={(e) => setObservacoes(e.target.value)}
            placeholder="Ex.: observações gerais da execução..."
          />
        </div>

        <div style={{ marginTop: 16 }}>
          <label style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>
            Fotos (opcional)
          </label>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
            <div
              style={{
                padding: 12,
                borderRadius: 8,
                border: "2px dashed rgba(148,163,184,0.5)",
                background: "rgba(148,163,184,0.08)",
                textAlign: "center",
                cursor: uploadandoFotos ? "not-allowed" : "pointer",
                opacity: uploadandoFotos ? 0.6 : 1,
              }}
            >
              <input
                type="file"
                multiple
                accept="image/jpeg,image/jpg,image/png,image/webp"
                onChange={handleAdicionarFotos}
                disabled={uploadandoFotos}
                style={{ display: "none" }}
                id="checklist-os-input-fotos-galeria"
              />
              <label htmlFor="checklist-os-input-fotos-galeria" style={{ cursor: uploadandoFotos ? "not-allowed" : "pointer", display: "block" }}>
                {uploadandoFotos ? "Enviando..." : (
                  <span>
                    🖼️ Galeria
                    <br />
                    <small>Selecionar fotos</small>
                  </span>
                )}
              </label>
            </div>

            <div
              style={{
                padding: 12,
                borderRadius: 8,
                border: "2px dashed rgba(148,163,184,0.5)",
                background: "rgba(148,163,184,0.08)",
                textAlign: "center",
                cursor: uploadandoFotos ? "not-allowed" : "pointer",
                opacity: uploadandoFotos ? 0.6 : 1,
              }}
            >
              <input
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                capture="environment"
                onChange={handleAdicionarFotos}
                disabled={uploadandoFotos}
                style={{ display: "none" }}
                id="checklist-os-input-fotos-camera"
              />
              <label htmlFor="checklist-os-input-fotos-camera" style={{ cursor: uploadandoFotos ? "not-allowed" : "pointer", display: "block" }}>
                {uploadandoFotos ? "Enviando..." : (
                  <span>
                    📸 Câmera
                    <br />
                    <small>Tirar foto</small>
                  </span>
                )}
              </label>
            </div>
          </div>

          {fotos.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>
                {fotos.length} foto(s) adicionada(s)
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(100px, 1fr))", gap: 8 }}>
                {fotos.map((foto, idx) => (
                  <div
                    key={idx}
                    style={{
                      position: "relative",
                      borderRadius: 8,
                      overflow: "hidden",
                      border: "1px solid rgba(148,163,184,0.35)",
                    }}
                  >
                    <img
                      src={foto.url}
                      alt={`Foto ${idx + 1}`}
                      style={{ width: "100%", height: 100, objectFit: "cover", display: "block" }}
                    />
                    <button
                      type="button"
                      onClick={() => removerFoto(idx)}
                      style={{
                        position: "absolute",
                        top: 4,
                        right: 4,
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        background: "rgba(239, 68, 68, 0.9)",
                        border: "none",
                        color: "white",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 12,
                      }}
                      title="Remover foto"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {!loading && os && itensChecklist.length > 0 && (
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 10 }}>
          <button type="button" className="pc-btn-primario" onClick={salvar} disabled={salvando}>
            {salvando ? "Salvando..." : "Salvar"}
          </button>
        </div>
      )}
    </section>
  );

  if (isMobile) {
    return <LayoutMobile titulo="Checklist" conteudo={body} />;
  }
  return <LayoutDesktop titulo="Checklist" conteudo={body} />;
}
